<script setup>
import { ref, onBeforeMount } from 'vue';
import { useAuthStore } from '@stores/AuthStore';
import { useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import { useI18n } from 'vue-i18n';
import Logo from '../../assets/logo.svg';
import LogoLite from '@assets/logo-lite.svg';
import LanguageSwitcher from '@components/global/LanguageSwitcher.vue';

const { t } = useI18n({ useScope: 'global' });
const username = ref(null);
const password = ref(null);

const isLoginLoading = ref(false);

const router = useRouter();
const authStore = useAuthStore();
const toast = useToast();

const redirectIsAuth = () => {
    if (authStore.isAuthenticated) {
        router.push('/');
    }
};

const login = async () => {
    try {
        if (!authStore.isAuthenticated && username.value && password.value) {
            if (isLoginLoading.value) return;

            isLoginLoading.value = true;
            await authStore.login(username.value, password.value);
            isLoginLoading.value = false;
            redirectIsAuth();
        } else {
            toast.add({ severity: 'error', summary: t('global.fillField'), life: 3000 });
        }
    } catch (er) {
        console.log(er);
    }

    username.value = null;
    password.value = null;
};

onBeforeMount(() => {
    redirectIsAuth();
});
</script>

<template>
    <div class="login">
        <div class="login-container">
            <div class="login-card">
                <LogoLite />
                <Form class="flex flex-col gap-4 p-4 min-w-96 -mt-28">
                    <h1 class="title">{{ t('auth.enter') }}</h1>
                    <span class="sub-title">{{ t('auth.subTitle') }}</span>

                    <FormField v-slot="$field" as="section" name="username" initialValue="" class="flex flex-col gap-2">
                        <FloatLabel variant="on">
                            <InputText class="w-full" id="username" size="large" v-model="username" />
                            <label for="username">{{ t('auth.username') }}</label>
                            <Message v-if="$field?.invalid" severity="error" size="small" variant="simple">{{ $field.error?.message }}</Message>
                        </FloatLabel>

                        <!-- <InputText v-model="username" type="text" :placeholder="t('auth.username')" /> -->
                    </FormField>
                    <FormField v-slot="$field" asChild name="password" initialValue="">
                        <section class="flex flex-col gap-2">
                            <FloatLabel variant="on">
                                <Password v-model="password" type="text" size="large" :feedback="false" toggleMask fluid />
                                <label for="password">{{ t('auth.password') }}</label>
                                <Message v-if="$field?.invalid" severity="error" size="small" variant="simple">{{ $field.error?.message }}</Message>
                            </FloatLabel>

                            <Message v-if="$field?.invalid" severity="error" size="small" variant="simple">{{ $field.error?.message }}</Message>
                        </section>
                    </FormField>
                    <Button type="submit" size="large" severity="primary" :label="t('auth.enter')" :loading="isLoginLoading" @click="login" />
                </Form>
                <div class="w-full flex items-center justify-between">
                    <p class="copyright">Proctoring Dashboard &copy; {{ new Date().getFullYear() }}</p>
                    <LanguageSwitcher type="login" />
                </div>
            </div>
            <div class="right-panel">
                <Logo class="logo" />
                <div class="sub-title-lite">{{ t('auth.about') }}</div>
            </div>
        </div>
    </div>
    <Toast />
</template>

<style scoped>
.login {
    @apply bg-[url('../../assets/images/login-bg.jpg')] bg-no-repeat bg-center bg-cover h-screen w-screen;
}

.login-container {
    @apply flex justify-between;
}

.logo {
    @apply justify-self-center w-[400px];
}

.login-card {
    @apply w-2/5 h-svh rounded-none   bg-white p-6 flex flex-col justify-between;
}

.title {
    @apply font-semibold text-5xl text-center;
}
.sub-title {
    @apply text-center  text-sm mb-10;
}
.inputs {
    @apply flex gap-4 flex-col mb-7;
}
.sub-title-lite {
    @apply text-center text-xl text-[--primary-light] mt-4;
}
.inputs input {
    @apply bg-[--bg-gray] text-sm px-3 py-2.5 rounded-xl;
}

.inputs input:focus {
    outline: none;
}

.forgot {
    @apply text-right text-sm text-[--inprogress-bg];
}

.login-btn {
    @apply bg-[--main-black] text-[--primary-light] text-lg text-center p-2 rounded-3xl w-full h-[44px] flex items-center justify-center;
}

.sign-up {
    @apply text-sm text-center text-zinc-500 mt-7;
}

.sign-up-link {
    @apply text-sm text-[--inprogress-bg];
}

.project-name {
    @apply text-6xl font-bold text-[--primary-light];
}

.password-eye {
    @apply absolute right-4 top-1/2 transform -translate-y-1/2;
}

.password-eye svg {
    @apply w-5 h-5;
}

.bottom {
    @apply flex justify-between items-center gap-4 absolute -translate-x-2/4 left-2/4 bottom-12;
}

.copyright {
    @apply text-center text-sm text-zinc-500;
}

.password-input {
    @apply relative;
}

.error-text {
    @apply mt-2 text-sm text-red-600;
}

.spinner {
    @apply w-5 h-5 rounded-full animate-spin border-[3px] border-solid border-white border-t-transparent;
}
.right-panel {
    @apply flex w-full h-svh items-center justify-center flex-col;
}
@media (max-width: 768px) {
    .right-panel {
        @apply hidden;
    }
    .login-card {
        @apply w-full h-svh rounded-none bg-white p-6 flex flex-col justify-between;
    }
}

@media (max-width: 1024px) {
}
</style>
